import { api } from './api';

export type AccountType =
  | 'checking'
  | 'savings'
  | 'brokerage'
  | 'wallet'
  | 'credit_card'
  | 'voucher'
  | 'other';

export type CategoryKind = 'income' | 'expense' | 'both';
export type Direction = 'in' | 'out' | 'transfer';
export type TransactionStatus = 'pending' | 'cleared' | 'reconciled';

export type PaymentMethod =
  | 'cash'
  | 'pix'
  | 'debit_card'
  | 'credit_card'
  | 'voucher'
  | 'transfer'
  | 'boleto'
  | 'other';

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  pix: 'Pix',
  debit_card: 'Débito',
  credit_card: 'Crédito',
  cash: 'Dinheiro',
  boleto: 'Boleto',
  transfer: 'TED/DOC',
  voucher: 'VR/VA',
  other: 'Outro',
};

export const ALL_PAYMENT_METHODS: PaymentMethod[] = [
  'pix',
  'debit_card',
  'credit_card',
  'cash',
  'boleto',
  'transfer',
  'voucher',
  'other',
];

export interface Institution {
  id: string;
  name: string;
  type: 'bank' | 'broker' | 'fintech' | 'other';
  logo_url: string | null;
  is_system: boolean;
  household_id: string | null;
}

export interface Category {
  id: string;
  name: string;
  kind: CategoryKind;
  parent_id: string | null;
  icon: string | null;
  color: string | null;
  is_system: boolean;
  sort_order: number;
  household_id: string | null;
}

export interface Account {
  id: string;
  name: string;
  type: AccountType;
  institution_id: string | null;
  currency: string;
  opening_balance: string;
  is_shared: boolean;
  is_archived: boolean;
  notes: string | null;
  supported_methods: PaymentMethod[];
  owner_user_id: string;
  created_at: string;
}

export interface AccountWithBalance extends Account {
  balance: string;
}

export interface Transaction {
  id: string;
  description: string;
  amount: string;
  direction: Direction;
  effective_date: string;
  competence_date: string;
  account_id: string;
  category_id: string | null;
  payment_method: PaymentMethod | null;
  owner_user_id: string;
  is_shared: boolean;
  status: TransactionStatus;
  source: string;
  notes: string | null;
  currency: string;
  created_at: string;
}

export interface TransactionPage {
  items: Transaction[];
  total: number;
  limit: number;
  offset: number;
}

export interface CategorySummary {
  category_id: string | null;
  category_name: string;
  total: string;
  count: number;
}

export interface MonthlySeriesPoint {
  month: string;
  income: string;
  expense: string;
}

export interface DashboardSummary {
  accounts_total_balance: string;
  month_income: string;
  month_expense: string;
  month_net: string;
  top_categories_expense: CategorySummary[];
  monthly_series: MonthlySeriesPoint[];
  accounts: AccountWithBalance[];
}

// ---------- calls ----------

export const listInstitutions = () => api<Institution[]>('/api/v1/institutions');
export const createInstitution = (body: {
  name: string;
  type: Institution['type'];
  logo_url?: string | null;
}) => api<Institution>('/api/v1/institutions', { method: 'POST', body: JSON.stringify(body) });

export const listCategories = () => api<Category[]>('/api/v1/categories');
export const createCategory = (body: {
  name: string;
  kind: CategoryKind;
  parent_id?: string | null;
  icon?: string | null;
  color?: string | null;
  sort_order?: number;
}) => api<Category>('/api/v1/categories', { method: 'POST', body: JSON.stringify(body) });

export const listAccounts = () => api<AccountWithBalance[]>('/api/v1/accounts');
export const createAccount = (body: {
  name: string;
  type: AccountType;
  institution_id?: string | null;
  opening_balance?: string;
  is_shared?: boolean;
  notes?: string | null;
  supported_methods?: PaymentMethod[];
}) => api<Account>('/api/v1/accounts', { method: 'POST', body: JSON.stringify(body) });
export const updateAccount = (id: string, body: Record<string, unknown>) =>
  api<Account>(`/api/v1/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteAccount = (id: string) =>
  api<void>(`/api/v1/accounts/${id}`, { method: 'DELETE' });

export interface TransactionFilters {
  account_id?: string;
  category_id?: string;
  payment_method?: PaymentMethod;
  direction?: Direction;
  status?: TransactionStatus;
  date_from?: string;
  date_to?: string;
  search?: string;
  only_mine?: boolean;
  only_shared?: boolean;
  limit?: number;
  offset?: number;
}

export const listTransactions = (filters: TransactionFilters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  });
  const qs = params.toString();
  return api<TransactionPage>(`/api/v1/transactions${qs ? `?${qs}` : ''}`);
};

export const createTransaction = (body: {
  description: string;
  amount: string;
  direction: 'in' | 'out';
  account_id: string;
  effective_date: string;
  competence_date?: string;
  category_id?: string | null;
  payment_method?: PaymentMethod | null;
  notes?: string | null;
  is_shared?: boolean;
}) => api<Transaction>('/api/v1/transactions', { method: 'POST', body: JSON.stringify(body) });

export const updateTransaction = (id: string, body: Record<string, unknown>) =>
  api<Transaction>(`/api/v1/transactions/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const deleteTransaction = (id: string) =>
  api<void>(`/api/v1/transactions/${id}`, { method: 'DELETE' });

export const createTransfer = (body: {
  description: string;
  amount: string;
  from_account_id: string;
  to_account_id: string;
  effective_date: string;
  notes?: string | null;
}) =>
  api<{ id: string }>('/api/v1/transactions/transfer', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const fetchDashboard = () => api<DashboardSummary>('/api/v1/reports/summary');

// ---------- formatters ----------

// ---------- nome derivado de conta ----------

export const SHORT_TYPE_LABEL: Record<AccountType, string> = {
  checking: 'Corrente',
  savings: 'Poupança',
  brokerage: 'Corretora',
  wallet: 'Carteira',
  credit_card: 'Crédito',
  voucher: 'VR/VA',
  other: 'Conta',
};

/** Padrão de nome: "<Instituição> <Tipo curto>" — sem instituição, só o tipo. */
export const deriveAccountName = (institutionName: string | null, type: AccountType): string =>
  institutionName ? `${institutionName} ${SHORT_TYPE_LABEL[type]}` : SHORT_TYPE_LABEL[type];

export const fmtBRL = (v: string | number): string => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export const fmtDate = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('pt-BR');
};
